import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Check, TrendingUp, ShoppingBag, ArrowRight, ArrowLeft,
  Loader2, Plus, Sparkles,
} from 'lucide-react';
import api from '@/api';

/**
 * HoldingsOnboarding (FB-PERS-1.1) — mandatory two-step wall after Stripe checkout.
 *
 * Step 1: pick mode(s) → Investor and/or Reseller. ≥1 required.
 * Step 2 (Investor): search 997-ticker universe → pick ≥3 stocks/crypto/forex.
 * Step 3 (Reseller): search scored_products → pick ≥3 OR free-text request.
 *
 * The free-text "we'll add it tomorrow" flow is wired for BOTH investor stocks
 * and reseller products via api.requestEntity().
 *
 * Hard wall — no skip. On submit calls /api/portfolio/onboard-holdings with
 * {modes, tracked_entities} and flips users.holdings_onboarding_complete=True.
 */

const MIN_PICKS = 3;

// ── Step 1: mode picker ───────────────────────────────────────────────────
function ModeStep({ modes, onToggle, onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14"
      data-testid="onboard-step-mode"
    >
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400/80 mb-3">Welcome to Premium</p>
      <h1 className="text-3xl sm:text-5xl font-black tracking-tighter leading-tight">
        How will you use WTFHappened?
      </h1>
      <p className="text-base text-neutral-400 mt-4 max-w-xl leading-relaxed">
        Pick one or both. We'll personalise your daily digest around what you actually trade.
      </p>

      <div className="mt-8 space-y-3" data-testid="mode-cards">
        <ModeCard
          active={modes.includes('investor')}
          onClick={() => onToggle('investor')}
          icon={TrendingUp}
          title="Investor"
          subtitle="Stocks, crypto, forex"
          example="Track Apple, Reckitt, Bitcoin, GBP/USD"
          testid="mode-investor"
        />
        <ModeCard
          active={modes.includes('reseller')}
          onClick={() => onToggle('reseller')}
          icon={ShoppingBag}
          title="Marketplace seller"
          subtitle="Trending products"
          example="Track sneakers, electronics, beauty drops, viral items"
          testid="mode-reseller"
        />
      </div>

      <div className="mt-8 sticky bottom-4">
        <button
          type="button"
          onClick={onContinue}
          disabled={modes.length === 0}
          className={`w-full py-4 rounded-md font-bold text-sm uppercase tracking-[0.18em] transition ${
            modes.length > 0
              ? 'bg-emerald-500 text-black hover:bg-emerald-400 active:scale-[0.98]'
              : 'bg-zinc-900 border border-zinc-800 text-neutral-600 cursor-not-allowed'
          }`}
          data-testid="mode-continue"
        >
          {modes.length === 0
            ? 'Pick at least one mode'
            : modes.length === 2
              ? 'Continue with both →'
              : `Continue as ${modes[0] === 'investor' ? 'Investor' : 'Marketplace seller'} →`}
        </button>
      </div>
    </motion.div>
  );
}

function ModeCard({ active, onClick, icon: Icon, title, subtitle, example, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full text-left rounded-lg border-2 p-5 transition flex items-start gap-4 active:scale-[0.99] ${
        active
          ? 'border-emerald-500 bg-emerald-500/10'
          : 'border-zinc-800 bg-zinc-950 hover:border-zinc-600'
      }`}
      data-testid={testid}
    >
      <div className={`shrink-0 w-12 h-12 rounded-md flex items-center justify-center ${
        active ? 'bg-emerald-500 text-black' : 'bg-zinc-900 text-neutral-400'
      }`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <div className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            active ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-700'
          }`}>
            {active && <Check size={14} className="text-black" />}
          </div>
        </div>
        <p className="text-sm text-neutral-300 mt-0.5">{subtitle}</p>
        <p className="text-xs text-neutral-500 mt-2 italic">{example}</p>
      </div>
    </button>
  );
}

// ── Step 2: Investor picker ───────────────────────────────────────────────
const INV_TABS = [
  { key: 'stock',  label: 'Stocks' },
  { key: 'crypto', label: 'Crypto' },
  { key: 'forex',  label: 'Forex' },
];
const REGION_FILTERS = {
  stock: [
    { key: 'all', label: 'All' },
    { key: 'US',  label: 'US' },
    { key: 'GB',  label: 'UK' },
    { key: 'IN',  label: 'India' },
  ],
  crypto: [{ key: 'all', label: 'All' }],
  forex:  [{ key: 'all', label: 'All' }],
};

function InvestorStep({ picks, onTogglePick, onBack, onContinue, isLast }) {
  const [activeTab, setActiveTab] = useState('stock');
  const [region, setRegion] = useState('all');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestNote, setRequestNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 60, asset_type: activeTab };
    if (region !== 'all') params.region = region;
    if (query.trim()) params.q = query.trim();
    api.getPortfolioUniverse(params)
      .then(res => { if (!cancelled) setResults(res.results || []); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, region, query]);

  const pickSet = useMemo(
    () => new Set(picks.filter(p => p.kind !== 'product').map(p => `${p.kind}:${p.symbol}`)),
    [picks],
  );
  const pickCount = picks.filter(p => p.kind !== 'product').length;
  const remaining = Math.max(0, MIN_PICKS - pickCount);
  const canContinue = pickCount >= MIN_PICKS;

  const handleRequest = async () => {
    const name = query.trim();
    if (name.length < 2) return;
    setRequesting(true);
    try {
      const res = await api.requestEntity('stock', name, region !== 'all' ? region : null);
      setRequestNote(res.message);
      setQuery('');
    } catch (e) {
      setRequestNote(e.response?.data?.detail || "Couldn't queue your request — try again.");
    } finally {
      setRequesting(false);
      setTimeout(() => setRequestNote(''), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      className="max-w-2xl mx-auto px-5 sm:px-8 py-8"
      data-testid="onboard-step-investor"
    >
      <BackButton onBack={onBack} />
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400/80 mb-3 mt-4">Investor picks</p>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tighter leading-tight">
        Pick {MIN_PICKS}+ things you actually trade.
      </h2>
      <CounterBar count={pickCount} remaining={remaining} testid="investor-counter" />

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-2 border-b border-zinc-800" data-testid="investor-tabs">
        {INV_TABS.map(t => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key} type="button"
              onClick={() => { setActiveTab(t.key); setRegion('all'); setQuery(''); }}
              className={`relative px-4 py-3 text-sm font-medium transition ${active ? 'text-white' : 'text-neutral-500 hover:text-neutral-300'}`}
              data-testid={`investor-tab-${t.key}`}
            >
              {t.label}
              {active && <motion.span layoutId="inv-tab-underline" className="absolute left-0 right-0 -bottom-px h-0.5 bg-white" />}
            </button>
          );
        })}
      </div>

      {/* Region filter (stocks only) */}
      {REGION_FILTERS[activeTab].length > 1 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {REGION_FILTERS[activeTab].map(r => (
            <button
              key={r.key} type="button" onClick={() => setRegion(r.key)}
              className={`px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider transition ${
                region === r.key ? 'bg-white text-black' : 'bg-zinc-900 text-neutral-400 hover:bg-zinc-800'
              }`}
              data-testid={`investor-region-${r.key}`}
            >{r.label}</button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="mt-5 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder={`Search ${activeTab}s by ticker or name…`}
          className="w-full pl-11 pr-4 py-3 bg-zinc-950 border border-zinc-800 rounded-md text-white placeholder:text-neutral-600 focus:outline-none focus:border-zinc-600 transition"
          data-testid="investor-search-input"
        />
      </div>

      {/* Results */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="investor-results">
        {loading && <div className="col-span-full text-center text-sm text-neutral-500 py-8">Loading…</div>}
        {!loading && results.length === 0 && (
          <NoResults
            query={query}
            kind="stock"
            onRequest={handleRequest}
            requesting={requesting}
            note={requestNote}
          />
        )}
        {results.map(item => {
          const key = `${item.asset_type}:${item.symbol}`;
          const picked = pickSet.has(key);
          return (
            <UniverseRow
              key={key} item={item} picked={picked}
              onClick={() => onTogglePick({
                kind: item.asset_type, symbol: item.symbol, name: item.name,
                asset_type: item.asset_type, region: item.region,
              })}
            />
          );
        })}
        {/* Always show the "request" CTA at the end of results too, in case user typed but got partial matches. */}
        {!loading && results.length > 0 && query.trim().length >= 2 && (
          <RequestRow
            kind="stock"
            name={query.trim()}
            onRequest={handleRequest}
            requesting={requesting}
            note={requestNote}
          />
        )}
      </div>

      <ContinueButton
        canContinue={canContinue}
        remaining={remaining}
        kind="investor"
        isLast={isLast}
        onClick={onContinue}
      />
    </motion.div>
  );
}

// ── Step 3: Reseller picker ───────────────────────────────────────────────
function ResellerStep({ picks, onTogglePick, onBack, onContinue, isLast }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestNote, setRequestNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getResellerUniverse(query.trim(), 40)
      .then(res => { if (!cancelled) setResults(res.results || []); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const pickSet = useMemo(
    () => new Set(picks.filter(p => p.kind === 'product').map(p => `product:${p.symbol}`)),
    [picks],
  );
  const pickCount = picks.filter(p => p.kind === 'product').length;
  const remaining = Math.max(0, MIN_PICKS - pickCount);
  const canContinue = pickCount >= MIN_PICKS;

  const handleRequest = async () => {
    const name = query.trim();
    if (name.length < 2) return;
    setRequesting(true);
    try {
      const res = await api.requestEntity('product', name);
      setRequestNote(res.message);
      // Auto-add the requested product to picks so user gets credit toward MIN.
      onTogglePick({
        kind: 'product',
        symbol: `request:${name.toLowerCase()}`,
        name,
        category: 'requested',
      });
      setQuery('');
    } catch (e) {
      setRequestNote(e.response?.data?.detail || "Couldn't queue your request — try again.");
    } finally {
      setRequesting(false);
      setTimeout(() => setRequestNote(''), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      className="max-w-2xl mx-auto px-5 sm:px-8 py-8"
      data-testid="onboard-step-reseller"
    >
      <BackButton onBack={onBack} />
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400/90 mb-3 mt-4">Reseller picks</p>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tighter leading-tight">
        Pick {MIN_PICKS}+ products you trade.
      </h2>
      <p className="text-sm text-neutral-400 mt-2">
        Can't find your product? Type the name and tap "Track this from tomorrow" — we'll add it to our scrape queue.
      </p>
      <CounterBar count={pickCount} remaining={remaining} testid="reseller-counter" />

      {/* Search */}
      <div className="mt-5 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search products (e.g. iPhone, sneakers, perfume…)"
          className="w-full pl-11 pr-4 py-3 bg-zinc-950 border border-zinc-800 rounded-md text-white placeholder:text-neutral-600 focus:outline-none focus:border-zinc-600 transition"
          data-testid="reseller-search-input"
        />
      </div>

      {/* Results */}
      <div className="mt-4 grid grid-cols-1 gap-2" data-testid="reseller-results">
        {loading && <div className="text-center text-sm text-neutral-500 py-8">Loading…</div>}
        {!loading && results.length === 0 && (
          <NoResults
            query={query}
            kind="product"
            onRequest={handleRequest}
            requesting={requesting}
            note={requestNote}
          />
        )}
        {results.map(item => {
          const key = `product:${item.id}`;
          const picked = pickSet.has(`product:${item.id}`);
          return (
            <ProductRow
              key={key} item={item} picked={picked}
              onClick={() => onTogglePick({
                kind: 'product', symbol: item.id, name: item.product, category: item.category,
              })}
            />
          );
        })}
        {/* Free-text request CTA when query has matches — encourages "Add custom" too */}
        {!loading && results.length > 0 && query.trim().length >= 2 && (
          <RequestRow
            kind="product"
            name={query.trim()}
            onRequest={handleRequest}
            requesting={requesting}
            note={requestNote}
          />
        )}
      </div>

      <ContinueButton
        canContinue={canContinue}
        remaining={remaining}
        kind="reseller"
        isLast={isLast}
        onClick={onContinue}
      />
    </motion.div>
  );
}

// ── Shared row + helper components ───────────────────────────────────────
function UniverseRow({ item, picked, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`group flex items-center justify-between gap-3 px-4 py-3 rounded-md border transition active:scale-[0.98] text-left ${
        picked ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-600 hover:bg-zinc-900'
      }`}
      data-testid={`universe-item-${item.symbol}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-white truncate">{item.symbol}</span>
          {item.region && item.region !== 'GLOBAL' && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-500 px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800">
              {item.region}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-400 truncate mt-0.5">{item.name}</div>
      </div>
      <div className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${
        picked ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-700'
      }`}>
        {picked && <Check size={14} className="text-black" />}
      </div>
    </button>
  );
}

function ProductRow({ item, picked, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`group flex items-center justify-between gap-3 px-4 py-3 rounded-md border transition active:scale-[0.98] text-left ${
        picked ? 'bg-amber-500/10 border-amber-500/40' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-600 hover:bg-zinc-900'
      }`}
      data-testid={`reseller-item-${item.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-white truncate">{item.product}</div>
        <div className="text-xs text-neutral-400 truncate mt-0.5 flex items-center gap-2">
          {item.category && (
            <span className="px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 uppercase tracking-wider text-[9px]">
              {item.category}
            </span>
          )}
          {typeof item.sell_score === 'number' && (
            <span className="font-mono">Sell {Math.round(item.sell_score)}/100</span>
          )}
        </div>
      </div>
      <div className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${
        picked ? 'bg-amber-500 border-amber-500' : 'border-zinc-700'
      }`}>
        {picked && <Check size={14} className="text-black" />}
      </div>
    </button>
  );
}

function NoResults({ query, kind, onRequest, requesting, note }) {
  const trimmed = (query || '').trim();
  return (
    <div className="col-span-full text-center py-8" data-testid="no-results">
      {trimmed.length >= 2 ? (
        <>
          <Sparkles size={16} className="mx-auto text-amber-400 mb-2" />
          <p className="text-sm text-neutral-300 mb-1">No matches for "<span className="font-bold text-white">{trimmed}</span>".</p>
          <p className="text-xs text-neutral-500 mb-4">We can start tracking it from tomorrow's scrape.</p>
          <button
            type="button" disabled={requesting}
            onClick={onRequest}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-md text-xs font-bold uppercase tracking-wider hover:bg-amber-400 transition active:scale-[0.98] disabled:opacity-60"
            data-testid={`request-${kind}-cta`}
          >
            {requesting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Track "{trimmed}" from tomorrow
          </button>
          {note && <p className="text-xs text-emerald-400 mt-3">{note}</p>}
        </>
      ) : (
        <p className="text-sm text-neutral-500">Type to search — or just browse.</p>
      )}
    </div>
  );
}

function RequestRow({ kind, name, onRequest, requesting, note }) {
  return (
    <div className="col-span-full mt-2 p-3 rounded-md border border-dashed border-zinc-800 bg-zinc-950/60">
      <div className="flex items-center gap-3 justify-between">
        <div className="text-xs text-neutral-500 italic">Don't see "{name}"?</div>
        <button
          type="button" disabled={requesting}
          onClick={onRequest}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-md text-[11px] font-bold uppercase tracking-wider hover:bg-amber-500/25 transition active:scale-[0.98] disabled:opacity-60"
          data-testid={`request-${kind}-inline-cta`}
        >
          {requesting ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
          Track from tomorrow
        </button>
      </div>
      {note && <p className="text-[11px] text-emerald-400 mt-2">{note}</p>}
    </div>
  );
}

function CounterBar({ count, remaining, testid }) {
  return (
    <div className="mt-4 flex items-center gap-3" data-testid={testid}>
      <span className="text-sm font-mono text-neutral-500">
        {count} / {MIN_PICKS}+ picked
      </span>
      {remaining > 0 ? (
        <span className="text-xs font-bold uppercase tracking-wider text-amber-400/90">
          {remaining} to go
        </span>
      ) : (
        <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Ready</span>
      )}
    </div>
  );
}

function BackButton({ onBack }) {
  return (
    <button
      type="button" onClick={onBack}
      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500 hover:text-neutral-300 transition"
      data-testid="onboard-back"
    >
      <ArrowLeft size={12} /> Back
    </button>
  );
}

function ContinueButton({ canContinue, remaining, kind, isLast, onClick }) {
  return (
    <div className="mt-8 sticky bottom-4">
      <button
        type="button" disabled={!canContinue}
        onClick={onClick}
        className={`w-full py-4 rounded-md font-bold text-sm uppercase tracking-[0.18em] transition flex items-center justify-center gap-2 ${
          canContinue
            ? 'bg-emerald-500 text-black hover:bg-emerald-400 active:scale-[0.98]'
            : 'bg-zinc-900 border border-zinc-800 text-neutral-600 cursor-not-allowed'
        }`}
        data-testid={`${kind}-continue`}
      >
        {!canContinue
          ? `Pick ${remaining} more to continue`
          : isLast ? 'Save & open WTFHappened →' : 'Next: reseller picks →'}
        {canContinue && <ArrowRight size={14} />}
      </button>
    </div>
  );
}

// ── Top-level container ───────────────────────────────────────────────────
export default function HoldingsOnboarding({ user, onComplete }) {
  // Local state
  const [step, setStep] = useState('mode');           // mode | investor | reseller | submitting
  const [modes, setModes] = useState([]);
  const [picks, setPicks] = useState([]);             // all picks across both modes
  const [error, setError] = useState('');

  const handleToggleMode = (mode) => {
    setModes(prev => prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]);
  };

  const handleTogglePick = (item) => {
    const key = `${item.kind}:${item.symbol}`;
    setPicks(prev => {
      const has = prev.some(p => `${p.kind}:${p.symbol}` === key);
      if (has) return prev.filter(p => `${p.kind}:${p.symbol}` !== key);
      if (prev.length >= 30) return prev; // soft cap
      return [...prev, item];
    });
  };

  const flow = useMemo(() => {
    const out = ['mode'];
    if (modes.includes('investor')) out.push('investor');
    if (modes.includes('reseller')) out.push('reseller');
    return out;
  }, [modes]);

  const goNext = async () => {
    const idx = flow.indexOf(step);
    if (idx < flow.length - 1) {
      setStep(flow[idx + 1]);
      window.scrollTo(0, 0);
      return;
    }
    // Final submit
    setStep('submitting');
    setError('');
    try {
      const res = await api.onboardHoldingsTwoMode(modes, picks);
      const stored = localStorage.getItem('wtf_user');
      if (stored) {
        const u = JSON.parse(stored);
        u.holdings_onboarding_complete = true;
        localStorage.setItem('wtf_user', JSON.stringify(u));
      }
      onComplete?.(res);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save your holdings. Try again.');
      // Revert to last visible step.
      setStep(flow[flow.length - 1]);
    }
  };

  const goBack = () => {
    const idx = flow.indexOf(step);
    if (idx > 0) {
      setStep(flow[idx - 1]);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="min-h-screen w-full pb-32 sm:pb-24" style={{ background: '#09090B', color: '#FAFAFA' }} data-testid="holdings-onboarding">
      <ProgressDots flow={flow} step={step} />
      <AnimatePresence mode="wait">
        {step === 'mode' && (
          <ModeStep key="mode" modes={modes} onToggle={handleToggleMode} onContinue={goNext} />
        )}
        {step === 'investor' && (
          <InvestorStep
            key="investor" picks={picks} onTogglePick={handleTogglePick}
            onBack={goBack} onContinue={goNext}
            isLast={!modes.includes('reseller')}
          />
        )}
        {step === 'reseller' && (
          <ResellerStep
            key="reseller" picks={picks} onTogglePick={handleTogglePick}
            onBack={goBack} onContinue={goNext}
            isLast={true}
          />
        )}
        {step === 'submitting' && (
          <motion.div
            key="submitting" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center min-h-screen px-6 text-center"
          >
            <Loader2 className="animate-spin text-emerald-400 mb-4" size={28} />
            <p className="text-sm text-neutral-400">Saving your holdings…</p>
          </motion.div>
        )}
      </AnimatePresence>
      {error && (
        <div className="fixed bottom-24 inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 max-w-md px-4 py-3 rounded-md bg-rose-500/10 border border-rose-500/40 text-rose-300 text-sm" data-testid="onboarding-error">
          {error}
        </div>
      )}
    </div>
  );
}

function ProgressDots({ flow, step }) {
  if (flow.length <= 1) return null;
  const idx = flow.indexOf(step);
  return (
    <div className="pt-6 flex items-center justify-center gap-2" data-testid="progress-dots">
      {flow.map((s, i) => (
        <span
          key={s}
          className={`block h-1 rounded-full transition-all ${
            i <= idx ? 'bg-emerald-500 w-8' : 'bg-zinc-800 w-4'
          }`}
        />
      ))}
    </div>
  );
}
